import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Button,
  Container,
  Input,
  Label,
} from "reactstrap";
import { toast } from "react-toastify";
import moment from "moment";

import Loaders from "components/Common/Loaders";
import { AsyncClientSelect } from "components/Common/AsyncSearchSelects";
import {
  fetchAiPipelineClientPipeline,
  fetchAiPipelineNeedsAttention,
  fetchAiPipelineSummary,
  fetchAiPipelineSummaryDetail,
} from "model/DashboardModel";
import {
  applyClientAsyncFilterOptionToFormState,
  buildClientAsyncFilterInitialOption,
  getClientAsyncFilterLabelFieldKey,
  normalizeMetadataFilterDateForApi,
} from "helpers/metadataFilterHelpers";
import { getCurrentServerTime } from "libs/context/serverTimeBridge";

import AiPipelineOutcomeModal from "./AiPipelineOutcomeModal";
import "./ai-dashboard.scss";
import { DEFAULT_PAGE_SIZE, urlEndPoint } from "helpers/GeneralHelper";


const STAGE_DOT_VARIANTS = {
  1: "blue",
  2: "orange",
  3: "purple",
  4: "green",
};

/** POST dashboard/ai-pipeline/summary-detail/filter — `summaryType` values. */
const AI_SUMMARY_DETAIL_TYPE = {
  TOTAL: "Total",
  TOTAL_TRIGGERED: "TotalTriggeredCandidates",
  GOOD_FIT: "GoodFit",
  EVALUATED: "Evaluated",
  NEEDS_FOLLOW_UP: "NeedsFollowUp",
  RTR_COMPLETED: "RtrCompleted",
  RTR_PENDING: "RtrFollowUp",
  FADV_INITIATED: "FadvInitiated",
  FADV_SENT: "FadvSent",
  FADV_FAILED: "FadvFailed",
  EMAIL_PENDING: "EmailPending",
};

const AI_SUMMARY_DETAIL_TITLES = {
  [AI_SUMMARY_DETAIL_TYPE.TOTAL]: "Total candidates",
  [AI_SUMMARY_DETAIL_TYPE.TOTAL_TRIGGERED]: "Total triggered candidates",
  [AI_SUMMARY_DETAIL_TYPE.GOOD_FIT]: "Good fit",
  [AI_SUMMARY_DETAIL_TYPE.EVALUATED]: "Evaluated",
  [AI_SUMMARY_DETAIL_TYPE.NEEDS_FOLLOW_UP]: "Needs follow-up",
  [AI_SUMMARY_DETAIL_TYPE.RTR_COMPLETED]: "RTR completed",
  [AI_SUMMARY_DETAIL_TYPE.RTR_PENDING]: "Pending RTR",
  [AI_SUMMARY_DETAIL_TYPE.FADV_INITIATED]: "FADV initiated",
  [AI_SUMMARY_DETAIL_TYPE.FADV_SENT]: "FADV sent",
  [AI_SUMMARY_DETAIL_TYPE.FADV_FAILED]: "FADV failed",
  [AI_SUMMARY_DETAIL_TYPE.EMAIL_PENDING]: "Email pending",
};

const POSITIVE_OUTCOME_KEYS = new Set([
  "GOOD_FIT",
  "GOODFIT",
  "CONNECTED",
  "CALL_COMPLETED",
  "CALLCOMPLETED",
  "COMPLETED",
]);

const NEGATIVE_OUTCOME_KEYS = new Set([
  "BAD_FIT",
  "BADFIT",
  "NO_ANS",
  "NO_ANSWER",
  "NOANS",
  "NO_ANS_DISC",
  "DISCONNECTED",
]);

const formatPercent = (value) => `${Number(value ?? 0)}%`;

const formatCount = (value) => String(Number(value ?? 0));

const getClientInitials = (name = "") => {
  const cleaned = String(name).replace(/\(.*?\)/g, "").trim();
  const words = cleaned.split(/\s+/).filter(Boolean);
  if (!words.length) return "?";
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return `${words[0][0] || ""}${words[1][0] || ""}`.toUpperCase();
};

const normalizeOutcomeKey = (outcome = {}) =>
  String(outcome?.outcomeKey ?? "")
    .trim()
    .toUpperCase()
    .replace(/\s+/g, "_");

const normalizeOutcomeLabel = (outcome = {}) =>
  String(outcome?.label ?? "")
    .trim()
    .toLowerCase();

const resolveOutcomeCountTone = (outcome = {}) => {
  const key = normalizeOutcomeKey(outcome);
  const label = normalizeOutcomeLabel(outcome);

  if (
    POSITIVE_OUTCOME_KEYS.has(key) ||
    label.includes("good fit") ||
    label.includes("connected") ||
    label.includes("call completed")
  ) {
    return "positive";
  }

  if (
    NEGATIVE_OUTCOME_KEYS.has(key) ||
    label.includes("bad fit") ||
    label.includes("no ans") ||
    label.includes("no answer") ||
    label.includes("disc")
  ) {
    return "negative";
  }

  return "default";
};

const resolveStageDotVariant = (stage = {}) => {
  const stageId = Number(stage?.stageId);
  if (STAGE_DOT_VARIANTS[stageId]) return STAGE_DOT_VARIANTS[stageId];

  const name = String(stage?.stageName ?? "").toLowerCase();
  if (name.includes("first call")) return "blue";
  if (name.includes("bg pending")) return "orange";
  if (name.includes("bg done") || name.includes("drug")) return "purple";
  if (name.includes("rtr")) return "green";
  return "blue";
};

const formatStageTitle = (stageName = "") => {
  const value = String(stageName ?? "").trim();
  if (!value) return "STAGE";
  if (/bg done/i.test(value)) return "BG DONE / DRUG PENDING";
  return value.toUpperCase();
};

const AI_DASHBOARD_DATE_RANGE_DAYS = 60;

const getAiDashboardServerMoment = () => moment(getCurrentServerTime());

const getAiDashboardMinDate = () =>
  getAiDashboardServerMoment()
    .subtract(AI_DASHBOARD_DATE_RANGE_DAYS, "days")
    .format("YYYY-MM-DD");

const getAiDashboardMaxDate = () => getAiDashboardServerMoment().format("YYYY-MM-DD");

const getDefaultAiDashboardDateRange = () => ({
  fromDate: getAiDashboardServerMoment().subtract(6, "days").format("YYYY-MM-DD"),
  toDate: getAiDashboardMaxDate(),
});

const getAiDashboardTodayDateRange = () => {
  const today = getAiDashboardMaxDate();
  return { fromDate: today, toDate: today };
};

const getAiDashboardYesterdayDateRange = () => {
  const yesterday = getAiDashboardServerMoment().subtract(1, "day").format("YYYY-MM-DD");
  return { fromDate: yesterday, toDate: yesterday };
};

const isSameAiDashboardDateRange = (range = {}, nextRange = {}) =>
  String(range?.fromDate ?? "") === String(nextRange?.fromDate ?? "") &&
  String(range?.toDate ?? "") === String(nextRange?.toDate ?? "");

const buildFilterPayload = (filterForm = {}) => {
  const clientId = String(filterForm?.clientId ?? "").trim();
  const fromDate = normalizeMetadataFilterDateForApi(filterForm?.fromDate);
  const toDate = normalizeMetadataFilterDateForApi(filterForm?.toDate);

  const payload = {};
  if (clientId) payload.clientId = clientId;
  if (fromDate) payload.fromDate = fromDate;
  if (toDate) payload.toDate = toDate;
  return payload;
};

const computeClientAttention = (client = {}) => {
  const redFlagMessages = (Array.isArray(client?.redFlagMessages) ? client.redFlagMessages : [])
    .map((message) => String(message ?? "").trim())
    .filter(Boolean);

  return {
    needsAttention: redFlagMessages.length > 0,
    redFlagMessages,
  };
};

const openBadFitPageInNewTab = () => {
  window.open(`/${urlEndPoint.badFit}?from=ai-dashboard`, "_blank", "noopener,noreferrer");
};

const buildSummaryMeta = (summary = {}) => {
  const evaluated = Number(summary?.evaluatedCandidates ?? 0);
  const totalTriggered = Number(summary?.totalTriggeredCandidates ?? 0);
  const goodFit = Number(summary?.goodFitCandidates ?? 0);
  const rtrPending = Number(summary?.rtrFollowUpCount ?? 0);
  const fadvSent = Number(summary?.fadvSentCount ?? 0);
  const fadvFailed = Number(summary?.fadvFailedCount ?? 0);
  const emailPending = Number(summary?.emailPendingCount ?? 0);

  const fromDate = summary?.fromDate
    ? moment(summary.fromDate).format("MMM D")
    : "";
  const toDate = summary?.toDate ? moment(summary.toDate).format("MMM D") : "";
  const dateRange =
    fromDate && toDate ? `${fromDate} – ${toDate}` : "Current period";

  return {
    counts: {
      evaluated,
      totalTriggered,
      goodFit,
      rtrPending,
      fadvSent,
      fadvFailed,
      emailPending,
    },
    totalCandidates: {
      meta: dateRange,
      tone: "green",
      icon: "bx bx-up-arrow-alt",
    },
    fadvInitiated: {
      tone: fadvFailed > 0 ? "red" : "green",
      icon: fadvFailed > 0 ? "bx bx-error-circle" : "bx bx-check",
    },
    goodFitRate: {
      meta:
        evaluated > 0
          ? `${formatCount(goodFit)} good fit of ${formatCount(evaluated)} evaluated`
          : "No evaluated candidates yet",
      tone: evaluated > 0 && goodFit < evaluated / 2 ? "red" : "green",
      icon: evaluated > 0 && goodFit < evaluated / 2 ? "bx bx-down-arrow-alt" : null,
    },
    needsFollowUp: {
      meta: "VM + no answer",
      tone: "red",
      icon: "bx bx-error-circle",
    },
    emailPending: {
      meta:
        emailPending > 0
          ? `${formatCount(emailPending)} awaiting email`
          : "No pending emails",
      tone: emailPending > 0 ? "red" : "green",
      icon: emailPending > 0 ? "bx bx-error-circle" : "bx bx-check",
    },
    rtrCompleted: {
      meta:
        rtrPending > 0
          ? `${formatCount(rtrPending)} pending RTR`
          : "All RTR calls completed",
      tone: "green",
      icon: "bx bx-check",
    },
  };
};

const SummaryMetaLink = ({ children, onClick, clickable = false, className = "" }) => {
  if (!clickable || typeof onClick !== "function") {
    return <span className={className || undefined}>{children}</span>;
  }

  const handleClick = (event) => {
    event.stopPropagation();
    onClick();
  };

  const handleKeyDown = (event) => {
    if (event.key !== "Enter" && event.key !== " ") return;
    event.preventDefault();
    handleClick(event);
  };

  return (
    <button
      type="button"
      className={`ai-summary-card__meta-link${className ? ` ${className}` : ""}`}
      title="View details"
      onClick={handleClick}
      onKeyDown={handleKeyDown}
    >
      {children}
    </button>
  );
};

const SummaryCard = ({
  label,
  value,
  meta,
  secondaryMeta,
  tone,
  icon,
  onValueClick,
  valueTitle,
}) => {
  const valueClickable = typeof onValueClick === "function";
  const showMeta = Boolean(meta) || Boolean(icon);
  const showSecondaryMeta = Boolean(secondaryMeta);

  const handleValueClick = (event) => {
    event.stopPropagation();
    onValueClick?.();
  };

  const handleValueKeyDown = (event) => {
    if (event.key !== "Enter" && event.key !== " ") return;
    event.preventDefault();
    handleValueClick(event);
  };

  return (
    <div className="ai-summary-card">
      <span className="ai-summary-card__label">{label}</span>
      {valueClickable ? (
        <button
          type="button"
          className="ai-summary-card__value ai-summary-card__value--clickable"
          title={valueTitle || "View details"}
          onClick={handleValueClick}
          onKeyDown={handleValueKeyDown}
        >
          {value}
        </button>
      ) : (
        <span className="ai-summary-card__value">{value}</span>
      )}
      {showMeta || showSecondaryMeta ? (
        <div className="ai-summary-card__meta-stack">
          {showSecondaryMeta ? secondaryMeta : null}
          {showMeta ? (
            <span className={`ai-summary-card__meta ai-summary-card__meta--${tone || "green"}`}>
              {icon ? <i className={icon} aria-hidden /> : null}
              {meta}
            </span>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};

const PipelineOutcomeRow = ({ outcome, onCountClick, clickable }) => {
  const tone = resolveOutcomeCountTone(outcome);
  const count = Number(outcome?.candidateCount ?? 0);
  const countClass = `ai-pipeline-outcome__count${
    tone === "positive"
      ? " ai-pipeline-outcome__count--positive"
      : tone === "negative"
        ? " ai-pipeline-outcome__count--negative"
        : ""
  }${clickable ? " ai-pipeline-outcome__count--clickable" : ""}`;

  const handleCountClick = (event) => {
    event.stopPropagation();
    if (!clickable || typeof onCountClick !== "function") return;
    onCountClick(outcome);
  };

  const handleCountKeyDown = (event) => {
    if (event.key !== "Enter" && event.key !== " ") return;
    event.preventDefault();
    handleCountClick(event);
  };

  return (
    <li className="ai-pipeline-outcome">
      <span className="ai-pipeline-outcome__label">{outcome?.label || "—"}</span>
      {clickable ? (
        <button
          type="button"
          className={countClass}
          onClick={handleCountClick}
          onKeyDown={handleCountKeyDown}
        >
          {formatCount(count)}
        </button>
      ) : (
        <span className={countClass}>{formatCount(count)}</span>
      )}
    </li>
  );
};

const ClientStatsRow = ({ client = {} }) => {
  const totalCandidates = Number(client?.totalCandidates ?? 0);
  const goodFitRate = Number(client?.goodFitRatePercent ?? 0);
  const fadvInitiated = Number(client?.fadvInitiatedCount ?? 0);
  const fadvSent = Number(client?.fadvSentCount ?? 0);
  const fadvFailed = Number(client?.fadvFailedCount ?? 0);

  return (
    <p className="ai-client-card__stats">
      <span className="ai-client-card__stat">
        <strong>{formatCount(totalCandidates)}</strong> candidates
      </span>
      <span className="ai-client-card__stat-sep" aria-hidden>
        ·
      </span>
      <span className="ai-client-card__stat ai-client-card__stat--good-fit">
        <strong>{formatPercent(goodFitRate)}</strong> good fit
      </span>
      <span className="ai-client-card__stat-sep" aria-hidden>
        ·
      </span>
      <span className="ai-client-card__stat ai-client-card__stat--fadv-initiated">
        <strong>{formatCount(fadvInitiated)}</strong> FADV initiated
      </span>
      <span className="ai-client-card__stat-sep" aria-hidden>
        ·
      </span>
      <span className="ai-client-card__stat ai-client-card__stat--fadv-sent">
        <strong>{formatCount(fadvSent)}</strong> sent
      </span>
      <span className="ai-client-card__stat-sep" aria-hidden>
        ·
      </span>
      <span
        className={`ai-client-card__stat ai-client-card__stat--fadv-failed${
          fadvFailed > 0 ? " ai-client-card__stat--fadv-failed-active" : ""
        }`}
      >
        <strong>{formatCount(fadvFailed)}</strong> failed
      </span>
    </p>
  );
};

const ClientPipelineCard = ({ client, expanded, onToggle, onOutcomeCountClick }) => {
  const attention = useMemo(() => computeClientAttention(client), [client]);
  const stages = Array.isArray(client?.stages) ? client.stages : [];

  return (
    <div className="ai-client-card">
      <div
        className="ai-client-card__header"
        role="button"
        tabIndex={0}
        onClick={onToggle}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            onToggle();
          }
        }}
      >
        <span className="ai-client-card__avatar" aria-hidden>
          {getClientInitials(client?.clientName)}
        </span>
        <div className="ai-client-card__info">
          <h5 className="ai-client-card__name">{client?.clientName || "—"}</h5>
          <ClientStatsRow client={client} />
        </div>
        <div className="ai-client-card__header-end">
          <span
            className={`ai-client-card__status ${
              attention.needsAttention
                ? "ai-client-card__status--attention"
                : "ai-client-card__status--track"
            }`}
          >
            <i
              className={attention.needsAttention ? "bx bx-error" : "bx bx-check"}
              aria-hidden
            />
            {attention.needsAttention
              ? `Needs attention (${attention.redFlagMessages.length})`
              : "On track"}
          </span>
          <i
            className={`bx ${expanded ? "bx-chevron-up" : "bx-chevron-down"} ai-client-card__chevron`}
            aria-hidden
          />
        </div>
      </div>

      {expanded ? (
        <>
          <div className="ai-client-card__body">
            <div className="ai-pipeline-grid">
              {stages.map((stage, stageIndex) => (
                <div
                  key={`${client?.clientId || client?.clientName}-${stage?.stageId || stage?.stageName}`}
                  className={`ai-pipeline-stage${
                    stageIndex < stages.length - 1 ? " ai-pipeline-stage--divider" : ""
                  }`}
                >
                  <h6 className="ai-pipeline-stage__title">
                    <span
                      className={`ai-pipeline-stage__dot ai-pipeline-stage__dot--${resolveStageDotVariant(stage)}`}
                      aria-hidden
                    />
                    {formatStageTitle(stage?.stageName)}
                  </h6>
                  <ul className="ai-pipeline-outcomes">
                    {(stage?.outcomes || []).map((outcome) => {
                      const outcomeCount = Number(outcome?.candidateCount ?? 0);
                      return (
                        <PipelineOutcomeRow
                          key={`${stage?.stageId}-${outcome?.outcomeKey || outcome?.label}`}
                          outcome={outcome}
                          clickable={outcomeCount > 0}
                          onCountClick={(selectedOutcome) =>
                            onOutcomeCountClick?.({
                              client,
                              stage,
                              outcome: selectedOutcome,
                            })
                          }
                        />
                      );
                    })}
                  </ul>
                </div>
              ))}
            </div>
          </div>

          {attention.redFlagMessages.length ? (
            <div className="ai-client-card__alert">
              {attention.redFlagMessages.map((message, index) => (
                <div className="ai-client-card__alert-item" key={`${index}-${message}`}>
                  <i className="bx bx-info-circle" aria-hidden />
                  <span>{message}</span>
                </div>
              ))}
            </div>
          ) : null}
        </>
      ) : null}
    </div>
  );
};

const AIDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [clients, setClients] = useState([]);
  const [filterForm, setFilterForm] = useState(() => ({
    clientId: "",
    ...getDefaultAiDashboardDateRange(),
  }));
  const [filterMenuOpen, setFilterMenuOpen] = useState(false);
  const [dateFilterDraft, setDateFilterDraft] = useState(() => getDefaultAiDashboardDateRange());
  const datePickerRef = useRef(null);
  const [expandedClientIds, setExpandedClientIds] = useState([]);
  const [outcomeModalOpen, setOutcomeModalOpen] = useState(false);
  const [outcomeModalLoading, setOutcomeModalLoading] = useState(false);
  const [outcomeModalResponse, setOutcomeModalResponse] = useState(null);
  const [outcomeModalTitle, setOutcomeModalTitle] = useState("Candidates");
  const [outcomeModalContext, setOutcomeModalContext] = useState(null);
  const [outcomeModalPagination, setOutcomeModalPagination] = useState({
    pageNumber: 1,
    pageSize: DEFAULT_PAGE_SIZE,
    totalPages: 0,
    totalRecords: 0,
  });

  const loadDashboard = useCallback(async (nextFilterForm = {}) => {
    setLoading(true);
    try {
      const payload = buildFilterPayload(nextFilterForm);
      const [summaryResponse, pipelineResponse] = await Promise.all([
        fetchAiPipelineSummary(payload),
        fetchAiPipelineClientPipeline(payload),
      ]);

      if (summaryResponse?.success === false) {
        toast.error(summaryResponse?.message || "Unable to load AI pipeline summary");
        setSummary(null);
      } else {
        setSummary(summaryResponse?.data ?? null);
      }

      if (pipelineResponse?.success === false) {
        toast.error(pipelineResponse?.message || "Unable to load client pipeline");
        setClients([]);
        setExpandedClientIds([]);
      } else {
        const nextClients = Array.isArray(pipelineResponse?.data) ? pipelineResponse.data : [];
        setClients(nextClients);
        setExpandedClientIds((previous) => {
          const validIds = new Set(
            nextClients.map((client) => String(client?.clientId ?? "").trim()).filter(Boolean)
          );
          const retained = previous.filter((clientId) => validIds.has(clientId));
          if (retained.length) return retained;
          const firstClientId = String(nextClients[0]?.clientId ?? "").trim();
          return firstClientId ? [firstClientId] : [];
        });
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadDashboard(filterForm);
  }, [filterForm, loadDashboard]);

  useEffect(() => {
    if (!filterMenuOpen) return;
    setDateFilterDraft({
      fromDate: filterForm?.fromDate || "",
      toDate: filterForm?.toDate || "",
    });
  }, [filterMenuOpen, filterForm?.fromDate, filterForm?.toDate]);

  useEffect(() => {
    if (!filterMenuOpen) return undefined;

    const handlePointerDown = (event) => {
      if (datePickerRef.current?.contains(event.target)) return;
      setFilterMenuOpen(false);
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("touchstart", handlePointerDown);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("touchstart", handlePointerDown);
    };
  }, [filterMenuOpen]);

  const summaryMeta = useMemo(
    () => buildSummaryMeta(summary),
    [summary]
  );

  const handleClientFilterChange = (option) => {
    setFilterForm((previous) =>
      applyClientAsyncFilterOptionToFormState(previous, option)
    );
  };

  const applyDateRangeFilter = useCallback((nextRange = {}, { closeMenu = true } = {}) => {
    const fromDate = nextRange?.fromDate || "";
    const toDate = nextRange?.toDate || "";
    setDateFilterDraft({ fromDate, toDate });
    setFilterForm((previous) => ({
      ...previous,
      fromDate,
      toDate,
    }));
    if (closeMenu) {
      setFilterMenuOpen(false);
    }
  }, []);

  const handleDateFilterChange = (event) => {
    const { name, value } = event.target;
    const nextDraft = {
      fromDate: name === "fromDate" ? value : dateFilterDraft?.fromDate || "",
      toDate: name === "toDate" ? value : dateFilterDraft?.toDate || "",
    };
    setDateFilterDraft(nextDraft);

    if (value && nextDraft.fromDate && nextDraft.toDate) {
      applyDateRangeFilter(nextDraft, { closeMenu: false });
    }
  };

  const handleTodayFilterClick = () => {
    applyDateRangeFilter(getAiDashboardTodayDateRange());
  };

  const handleYesterdayFilterClick = () => {
    applyDateRangeFilter(getAiDashboardYesterdayDateRange());
  };

  const handleDayFilterResetClick = () => {
    const nextRange = getDefaultAiDashboardDateRange();
    setDateFilterDraft(nextRange);
    setFilterForm((previous) => ({
      ...previous,
      fromDate: nextRange.fromDate,
      toDate: nextRange.toDate,
    }));
    setFilterMenuOpen(true);
  };

  const isTodayFilterActive = isSameAiDashboardDateRange(
    filterForm,
    getAiDashboardTodayDateRange()
  );
  const isYesterdayFilterActive = isSameAiDashboardDateRange(
    filterForm,
    getAiDashboardYesterdayDateRange()
  );

  const toggleClientExpanded = (clientId) => {
    const normalizedId = String(clientId ?? "").trim();
    if (!normalizedId) return;
    setExpandedClientIds((previous) =>
      previous.includes(normalizedId)
        ? previous.filter((id) => id !== normalizedId)
        : [...previous, normalizedId]
    );
  };

  const closeOutcomeModal = useCallback(() => {
    setOutcomeModalOpen(false);
    setOutcomeModalLoading(false);
    setOutcomeModalResponse(null);
    setOutcomeModalTitle("Candidates");
    setOutcomeModalContext(null);
    setOutcomeModalPagination({
      pageNumber: 1,
      pageSize: DEFAULT_PAGE_SIZE,
      totalPages: 0,
      totalRecords: 0,
    });
  }, []);

  const fetchOutcomeModalCandidates = useCallback(
    async (context, paginationOverrides = {}) => {
      const pageNumber = Number(paginationOverrides.pageNumber) || 1;
      const pageSize = Number(paginationOverrides.pageSize) || DEFAULT_PAGE_SIZE;
      const summaryType = String(context?.summaryType ?? "").trim();

      setOutcomeModalLoading(true);
      try {
        let response;

        if (summaryType) {
          response = await fetchAiPipelineSummaryDetail({
            ...buildFilterPayload(filterForm),
            summaryType,
            pageNumber,
            pageSize,
          });
        } else {
          const client = context?.client;
          const stage = context?.stage;
          const outcome = context?.outcome;
          const clientId = String(client?.clientId ?? "").trim();
          const stageId = Number(stage?.stageId);
          if (!clientId || !Number.isFinite(stageId)) {
            toast.error("Unable to load candidates for this outcome");
            return false;
          }

          const labelKey = String(outcome?.outcomeKey ?? "").trim();
          response = await fetchAiPipelineNeedsAttention({
            ...buildFilterPayload(filterForm),
            stageId,
            clientId,
            pageNumber,
            pageSize,
            ...(labelKey ? { labelKey } : {}),
          });
        }

        // if (!response || response?.success === false) {
        //   toast.error(response?.message || "Unable to load candidates");
        //   return false;
        // }

        const responseData = response?.data ?? {};
        const records = Array.isArray(responseData?.records)
          ? responseData.records
          : Array.isArray(responseData)
            ? responseData
            : [];

        setOutcomeModalResponse(response ?? null);
        setOutcomeModalPagination({
          pageNumber: Number(responseData?.pageNumber) || pageNumber,
          pageSize: Number(responseData?.pageSize) || pageSize,
          totalPages: Number(responseData?.totalPages) || 0,
          totalRecords: Number(responseData?.totalRecords) || records.length,
        });
        return true;
      } finally {
        setOutcomeModalLoading(false);
      }
    },
    [filterForm]
  );

  const openOutcomeModal = useCallback(
    async (context = {}, title = "Candidates") => {
      setOutcomeModalTitle(title);
      setOutcomeModalResponse(null);
      setOutcomeModalContext(context);
      setOutcomeModalPagination({
        pageNumber: 1,
        pageSize: DEFAULT_PAGE_SIZE,
        totalPages: 0,
        totalRecords: 0,
      });
      setOutcomeModalOpen(true);

      const loaded = await fetchOutcomeModalCandidates(context, {
        pageNumber: 1,
        pageSize: DEFAULT_PAGE_SIZE,
      });
      if (!loaded) {
        setOutcomeModalOpen(false);
        setOutcomeModalContext(null);
      }
    },
    [fetchOutcomeModalCandidates]
  );

  const handleOutcomeCountClick = useCallback(
    async (context = {}) => {
      const titleParts = [
        context?.client?.clientName,
        context?.stage?.stageName,
        context?.outcome?.label,
      ]
        .map((value) => String(value ?? "").trim())
        .filter(Boolean);

      await openOutcomeModal(
        context,
        titleParts.length ? titleParts.join(" · ") : "Candidates"
      );
    },
    [openOutcomeModal]
  );

  const handleSummaryDetailClick = useCallback(
    async (summaryType) => {
      const type = String(summaryType ?? "").trim();
      if (!type) return;
      await openOutcomeModal(
        { summaryType: type },
        AI_SUMMARY_DETAIL_TITLES[type] || "Candidates"
      );
    },
    [openOutcomeModal]
  );

  const handleOutcomeModalPageChange = useCallback(
    async (page) => {
      if (!outcomeModalContext) return;
      await fetchOutcomeModalCandidates(outcomeModalContext, {
        pageNumber: page,
        pageSize: outcomeModalPagination.pageSize,
      });
    },
    [fetchOutcomeModalCandidates, outcomeModalContext, outcomeModalPagination.pageSize]
  );

  const handleOutcomeModalPageSizeChange = useCallback(
    async (rows) => {
      if (!outcomeModalContext) return;
      await fetchOutcomeModalCandidates(outcomeModalContext, {
        pageNumber: 1,
        pageSize: rows,
      });
    },
    [fetchOutcomeModalCandidates, outcomeModalContext]
  );

  if (loading && !summary && !clients.length) {
    return <Loaders />;
  }

  return (
    <div className="page-content ai-dashboard-root">
      <Container fluid>
        <div className="ai-dashboard-header">
          <h4 className="ai-dashboard-title">AI pipeline dashboard</h4>
          <div className="ai-dashboard-header-actions">
            <div className="ai-dashboard-client-select">
              <AsyncClientSelect
                key={`ai-dashboard-client-${filterForm?.clientId || "all"}`}
                value={filterForm?.clientId || ""}
                initialOption={buildClientAsyncFilterInitialOption(
                  filterForm?.clientId,
                  filterForm[getClientAsyncFilterLabelFieldKey()]
                )}
                onChange={handleClientFilterChange}
                placeholder="All clients"
                isClearable
              />
            </div>
            <div className="ai-dashboard-day-filters" role="group" aria-label="Day filters">
              <Button
                type="button"
                color={isTodayFilterActive ? "primary" : "info"}
                outline={!isTodayFilterActive}
                size="md"
                className="ai-dashboard-day-filter-btn"
                onClick={handleTodayFilterClick}
              >
                Today
              </Button>
              <Button
                type="button"
                color={isYesterdayFilterActive ? "primary" : "info"}
                outline={!isYesterdayFilterActive}
                size="md"
                className="ai-dashboard-day-filter-btn"
                onClick={handleYesterdayFilterClick}
              >
                Yesterday
              </Button>
              <div className="ai-dashboard-date-picker" ref={datePickerRef}>
                <Button
                  type="button"
                  color={filterMenuOpen ? "primary" : "info"}
                  outline={!filterMenuOpen}
                  size="md"
                  className="ai-dashboard-day-filter-btn"
                  onClick={handleDayFilterResetClick}
                >
                  Date Range
                </Button>
                {filterMenuOpen ? (
                  <div className="ai-dashboard-filter-menu" role="dialog" aria-label="Date range">
                    <div className="ai-dashboard-filter-menu__body">
                      <p className="ai-dashboard-filter-menu__title">Date range</p>
                      <div className="ai-dashboard-filter-menu__field">
                        <Label
                          for="ai-dashboard-from-date"
                          className="ai-dashboard-filter-menu__label"
                        >
                          From
                        </Label>
                        <Input
                          type="date"
                          id="ai-dashboard-from-date"
                          name="fromDate"
                          className="ai-dashboard-filter-menu__input"
                          min={getAiDashboardMinDate()}
                          max={dateFilterDraft?.toDate || getAiDashboardMaxDate()}
                          value={dateFilterDraft?.fromDate || ""}
                          onChange={handleDateFilterChange}
                        />
                      </div>
                      <div className="ai-dashboard-filter-menu__field">
                        <Label
                          for="ai-dashboard-to-date"
                          className="ai-dashboard-filter-menu__label"
                        >
                          To
                        </Label>
                        <Input
                          type="date"
                          id="ai-dashboard-to-date"
                          name="toDate"
                          className="ai-dashboard-filter-menu__input"
                          min={dateFilterDraft?.fromDate || getAiDashboardMinDate()}
                          max={getAiDashboardMaxDate()}
                          value={dateFilterDraft?.toDate || ""}
                          onChange={handleDateFilterChange}
                        />
                      </div>
                    </div>
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="py-4">
            <Loaders />
          </div>
        ) : (
          <>
            <div className="ai-summary-grid">
              <SummaryCard
                label="Total candidates"
                value={formatCount(summary?.totalCandidates)}
                meta={summaryMeta.totalCandidates.meta}
                tone={summaryMeta.totalCandidates.tone}
                icon={summaryMeta.totalCandidates.icon}
                secondaryMeta={
                  <span className="ai-summary-card__meta ai-summary-card__meta--inline ai-summary-card__meta--dark mb-1">
                    <i className="mdi mdi-phone-log" aria-hidden />
                    <span>Call / SMS :</span>
                    <strong className="ai-summary-card__meta-count">
                      {formatCount(summaryMeta.counts.totalTriggered)}
                    </strong>
                  </span>
                }
              />
              <SummaryCard
                label="Good fit rate"
                value={formatPercent(summary?.goodFitRatePercent)}
                tone={summaryMeta.goodFitRate.tone}
                icon={summaryMeta.goodFitRate.icon}
                onValueClick={() =>
                  handleSummaryDetailClick(AI_SUMMARY_DETAIL_TYPE.GOOD_FIT)
                }
                meta={
                  summaryMeta.counts.evaluated > 0 ? (
                    <>
                      <SummaryMetaLink
                        clickable
                        className="ai-summary-card__meta-link--green"
                        onClick={() =>
                          handleSummaryDetailClick(AI_SUMMARY_DETAIL_TYPE.GOOD_FIT)
                        }
                      >
                        {formatCount(summaryMeta.counts.goodFit)} good fit
                      </SummaryMetaLink>
                      <span className="ai-summary-card__meta-sep"> of </span>
                      <SummaryMetaLink
                        clickable
                        onClick={() =>
                          handleSummaryDetailClick(AI_SUMMARY_DETAIL_TYPE.EVALUATED)
                        }
                      >
                        {formatCount(summaryMeta.counts.evaluated)} evaluated
                      </SummaryMetaLink>
                    </>
                  ) : (
                    summaryMeta.goodFitRate.meta
                  )
                }
              />
              <SummaryCard
                label="Bad fit resolution"
                value={formatCount(summary?.badFitResolveCount)}
                onValueClick={openBadFitPageInNewTab}
                valueTitle="Open bad fit page"
              />
              <SummaryCard
                label="FADV initiated"
                value={formatCount(summary?.fadvInitiatedCount)}
                tone={summaryMeta.fadvInitiated.tone}
                icon={summaryMeta.fadvInitiated.icon}
                meta={
                  <>
                    <SummaryMetaLink
                      clickable
                      onClick={() =>
                        handleSummaryDetailClick(AI_SUMMARY_DETAIL_TYPE.FADV_SENT)
                      }
                    >
                      {formatCount(summaryMeta.counts.fadvSent)} sent
                    </SummaryMetaLink>
                    {" · "}
                    <SummaryMetaLink
                      clickable
                      onClick={() =>
                        handleSummaryDetailClick(AI_SUMMARY_DETAIL_TYPE.FADV_FAILED)
                      }
                    >
                      {formatCount(summaryMeta.counts.fadvFailed)} failed
                    </SummaryMetaLink>
                  </>
                }
              />
              <SummaryCard
                label="Email pending"
                value={formatCount(summary?.emailPendingCount)}
                tone={summaryMeta.emailPending.tone}
                icon={summaryMeta.emailPending.icon}
                onValueClick={() =>
                  handleSummaryDetailClick(AI_SUMMARY_DETAIL_TYPE.EMAIL_PENDING)
                }
                meta={
                  <SummaryMetaLink
                    clickable
                    onClick={() =>
                      handleSummaryDetailClick(AI_SUMMARY_DETAIL_TYPE.EMAIL_PENDING)
                    }
                  >
                    {summaryMeta.emailPending.meta}
                  </SummaryMetaLink>
                }
              />
              <SummaryCard
                label="Needs follow-up"
                value={formatCount(summary?.needsFollowUpCount)}
                meta={summaryMeta.needsFollowUp.meta}
                tone={summaryMeta.needsFollowUp.tone}
                icon={summaryMeta.needsFollowUp.icon}
                onValueClick={() =>
                  handleSummaryDetailClick(AI_SUMMARY_DETAIL_TYPE.NEEDS_FOLLOW_UP)
                }
              />
              <SummaryCard
                label="RTR completed"
                value={formatCount(summary?.rtrCompletedCount)}
                tone={summaryMeta.rtrCompleted.tone}
                icon={summaryMeta.rtrCompleted.icon}
                onValueClick={() =>
                  handleSummaryDetailClick(AI_SUMMARY_DETAIL_TYPE.RTR_COMPLETED)
                }
                meta={
                  summaryMeta.counts.rtrPending > 0 ? (
                    <SummaryMetaLink
                      clickable
                      onClick={() =>
                        handleSummaryDetailClick(AI_SUMMARY_DETAIL_TYPE.RTR_PENDING)
                      }
                    >
                      {formatCount(summaryMeta.counts.rtrPending)} pending RTR
                    </SummaryMetaLink>
                  ) : (
                    summaryMeta.rtrCompleted.meta
                  )
                }
              />
            </div>

            <div className="ai-client-list">
              {clients.length ? (
                clients.map((client) => {
                  const clientId = String(client?.clientId ?? "").trim();
                  return (
                    <ClientPipelineCard
                      key={clientId || client?.clientName}
                      client={client}
                      expanded={expandedClientIds.includes(clientId)}
                      onToggle={() => toggleClientExpanded(clientId)}
                      onOutcomeCountClick={handleOutcomeCountClick}
                    />
                  );
                })
              ) : (
                <div className="ai-empty-state">No client pipeline data found.</div>
              )}
            </div>
          </>
        )}
      </Container>

      <AiPipelineOutcomeModal
        isOpen={outcomeModalOpen}
        onClose={closeOutcomeModal}
        loading={outcomeModalLoading}
        response={outcomeModalResponse}
        title={outcomeModalTitle}
        currentPage={outcomeModalPagination.pageNumber}
        rowsPerPage={outcomeModalPagination.pageSize}
        totalPages={outcomeModalPagination.totalPages}
        totalRecords={outcomeModalPagination.totalRecords}
        onPageChange={handleOutcomeModalPageChange}
        onPageSizeChange={handleOutcomeModalPageSizeChange}
      />
    </div>
  );
};

export default AIDashboard;
